/* GlowMeds: inline Quick add panel on product cards. Adds via /cart/add.js and refreshes the
   theme's cart drawer or cart notification, then publishes PUB_SUB_EVENTS.cartUpdate. */
if (!customElements.get('glowmeds-quick-add')) {
  customElements.define(
    'glowmeds-quick-add',
    class GlowmedsQuickAdd extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready) return;
        this.dataset.ready = 'true';
        this.toggleButton = this.querySelector('.gm-qa__toggle');
        this.closeButton = this.querySelector('.gm-qa__close');
        this.message = this.querySelector('.gm-qa__msg');
        this.onDocClick = this.onDocClick.bind(this);

        this.toggleButton?.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.setOpen(!this.classList.contains('is-open'), true);
        });
        this.closeButton?.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.setOpen(false);
          this.toggleButton?.focus();
        });
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            const wasInside = this.contains(document.activeElement);
            this.setOpen(false);
            if (wasInside) this.toggleButton?.focus();
          }
        });
        this.querySelectorAll('.gm-qa__size').forEach((button) =>
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.add(button);
          })
        );
        // Clicks inside the panel must not fall through to the card link.
        this.querySelector('.gm-qa__panel')?.addEventListener('click', (e) => {
          if (!e.target.closest('a')) e.stopPropagation();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick);
      }

      setOpen(open, focusFirst) {
        this.classList.toggle('is-open', open);
        this.toggleButton?.setAttribute('aria-expanded', String(open));
        if (open) {
          document.querySelectorAll('glowmeds-quick-add.is-open').forEach((el) => el !== this && el.setOpen(false));
          document.addEventListener('click', this.onDocClick);
          // Phones: if the opened panel sits under the floating chat/teaser buttons, nudge it up.
          requestAnimationFrame(() => {
            const r = this.querySelector('.gm-qa__panel')?.getBoundingClientRect();
            if (r && window.matchMedia('(max-width: 749px)').matches && r.bottom > window.innerHeight - 90) {
              window.scrollBy({ top: r.bottom - (window.innerHeight - 90), behavior: 'smooth' });
            }
          });
          if (focusFirst) {
            const first = this.querySelector('.gm-qa__size:not(:disabled), .gm-qa__choose');
            requestAnimationFrame(() => first?.focus({ preventScroll: true }));
          }
        } else {
          document.removeEventListener('click', this.onDocClick);
        }
      }

      onDocClick(e) {
        if (!this.contains(e.target)) this.setOpen(false);
      }

      showMessage(text, success) {
        if (!this.message) return;
        this.message.classList.toggle('is-success', !!success);
        this.message.textContent = text;
        clearTimeout(this.messageTimer);
        this.messageTimer = setTimeout(() => (this.message.textContent = ''), success ? 2500 : 5000);
      }

      getCart() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      async add(button) {
        if (button.disabled || this.busy) return;
        this.busy = true;
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        this.showMessage('');

        const id = Number(button.dataset.variantId);
        const cart = this.getCart();
        const body = { id, quantity: 1 };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(button);
        }
        const addUrl = ((window.routes && window.routes.cart_add_url) || '/cart/add').replace(/\.js$/, '') + '.js';

        try {
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            const text = data.description || data.message || 'Sorry, that size could not be added.';
            this.showMessage(typeof text === 'string' ? text : 'Sorry, that size could not be added.');
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, { source: 'glowmeds-quick-add', productVariantId: id, errors: data.errors || data.description, message: data.message });
            }
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'glowmeds-quick-add', productVariantId: id, cartData: data });
          }

          button.classList.remove('is-loading');
          button.classList.add('is-added');
          this.showMessage('Added to cart', true);
          setTimeout(() => button.classList.remove('is-added'), 1500);

          if (cart && typeof cart.renderContents === 'function' && data.sections) {
            cart.classList.remove('is-empty');
            this.setOpen(false);
            cart.renderContents(data);
          } else if (!cart) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        } catch (error) {
          console.error(error);
          this.showMessage('Something went wrong. Please try again.');
        } finally {
          this.busy = false;
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        }
      }
    }
  );
}
